import { EDimensionKey, EReportControlId } from '~/presentation/reports/common/type';
import { buildReportQuery, getAnalyticModeFromSearchParams } from '../../utils';
import { useReportDateTime } from '~/presentation/reports/ui/SelectDate/hooks';
import { syncOrderByWithDimensions } from '../../utils/orderBy';
import { getCacheParameterKey } from '~/modules/analytics';
import { QUERY_KEY } from '~/presentation/reports/query';
import { useReportControlContext } from '../../contexts';
import { useSearchParams } from '@remix-run/react';
import { useGetVersion } from '../useGetVersion';
import { useEffect, useMemo } from 'react';

export const useReportParameter = () => {
  const { filters, metrics, dimensions, orderBy, timeDimension } = useReportControlContext();
  const { currentDates, compareCurrentDates } = useReportDateTime({
    hasCampaignFilter: filters[EReportControlId.TAB_CAMPAIGN],
  });
  const { versionIds } = useGetVersion({ currentDates });

  const [searchParams, setSearchParams] = useSearchParams();
  const mode = getAnalyticModeFromSearchParams(searchParams);

  const normalizedOrderBy = useMemo(() => {
    return syncOrderByWithDimensions(dimensions, orderBy);
  }, [dimensions, orderBy]);

  const parameter = useMemo(() => {
    const query = buildReportQuery({
      mode,
      metrics,
      sources: ['sessions'],
      dimensions,
      dateRange: currentDates,
      compareDateRange: compareCurrentDates,
      filters,
      orderBy: normalizedOrderBy,
      timeDimension,
      versionIds,
    });
    return { input: query };
  }, [
    versionIds,
    mode,
    metrics,
    dimensions,
    currentDates,
    compareCurrentDates,
    filters,
    normalizedOrderBy,
    timeDimension,
  ]);

  const enabled = useMemo(() => {
    const hasCampaign = !!filters[EReportControlId.TAB_CAMPAIGN];
    const hasVersion = dimensions.includes(EDimensionKey.VERSION);

    if ((hasVersion && !hasCampaign) || !compareCurrentDates) return false;

    return !!metrics.length;
  }, [metrics, filters, dimensions, compareCurrentDates]);

  useEffect(() => {
    if (!parameter.input) return;

    const newParams = new URLSearchParams(searchParams);
    newParams.set(QUERY_KEY, JSON.stringify(parameter.input));

    setSearchParams(newParams, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parameter]);

  return {
    parameterKey: getCacheParameterKey(parameter),
    parameter,
    enabled,
  };
};
